import type { Layer, SingleLayerStack } from 'generated/graphql';
import { RendererSupport } from 'generated/graphql';
import type { BaseImagingProvider } from '../ViewportDre/modules/imaging/BaseImagingProvider';
import { MultiStackImagingProvider } from '../ViewportDre/modules/imaging/MultiStackImagingProvider';
import type { Series, Stack } from '../ViewportsConfigurations/types';
import type { TStackProviders } from './ViewerContext';
import { UNICODE_GROUP_SEPARATOR } from 'utils/string';

// Prefix for composite stack SMIDs to distinguish from regular multi-layer (fusion) stacks
const COMPOSITE_PREFIX = 'composite:';

/** Returns true if the stack smid is a single layer stack */
export const isSingleLayerStack = (s: unknown): s is SingleLayerStack => {
  return !!s && typeof s === 'object' && (s as any).__typename === 'SingleLayerStack';
};

/** Returns true if the stack smid is a multi-layer (fusion) stack, not a composite stack */
export const isStackSmidMultiLayer = (stackSmid?: string | null): boolean => {
  return (
    stackSmid != null &&
    stackSmid.includes(UNICODE_GROUP_SEPARATOR) &&
    !stackSmid.startsWith(COMPOSITE_PREFIX)
  );
};

/** Returns true if the renderer is supported */
export const isSupportedRender = (stack: Nullable<Stack>, renderer: string): boolean => {
  if (stack && 'frames' in stack) {
    return (
      [RendererSupport.Supported, RendererSupport.BetaSupported].includes(
        // @ts-expect-error [EN-10263] - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'SupportedRenderers | { readonly __typename?: "SupportedRenderers" | undefined; readonly vtk: RendererSupport; } | { readonly __typename?: "SupportedRenderers" | undefined; readonly vtk: RendererSupport; }'.
        stack.supportedRenderers[renderer]
      ) ?? false
    );
  }

  return true;
};

/** Returns true if the renderer is supported as a beta feature */
export const isBetaSupportedRender = (stack: Nullable<Stack>, renderer: string): boolean => {
  // @ts-expect-error [EN-7967] - TS2339 - Property 'supportedRenderers' does not exist on type 'Stack'.
  if (stack?.supportedRenderers != null) {
    // @ts-expect-error [EN-7967] - TS2339 - Property 'supportedRenderers' does not exist on type 'Stack'.
    return stack.supportedRenderers[renderer] === RendererSupport.BetaSupported;
  }

  return false;
};

/** Returns true if the first frame has pixels */
export const isPixels = (stack?: Stack | null): boolean => {
  if (stack && 'frames' in stack) {
    return stack.frames[0].hasPixels;
  } else if (stack?.stackLayers != null) {
    return true;
  }

  return false;
};

/** Returns true if all frames has pixels */
export const allPixels = (stack?: Stack | null): boolean => {
  if (stack && 'frames' in stack) {
    return stack.frames.every((frame) => frame.hasPixels);
  } else if (stack?.stackLayers != null) {
    return true;
  }

  return false;
};

/** Returns the stack image, if it exists */
export const getStackImage = (stack: Stack): string => {
  // @ts-expect-error [EN-7967] - TS2339 - Property 'image' does not exist on type 'Stack'.
  if (stack?.image != null) {
    // @ts-expect-error [EN-7967] - TS2339 - Property 'image' does not exist on type 'Stack'.
    return stack.image;
  }

  return '';
};

/** Finds a series by stack smid */
export const findSeriesByStackSmid = (
  stackSmid: string,
  imagingProvider?: BaseImagingProvider<Stack> | null
): Nullable<Series> => {
  if (imagingProvider == null) return;
  // @ts-expect-error [EN-7967] - TS2339 - Property 'series' does not exist on type '{ readonly __typename?: "LayeredStack"; readonly smid: string; readonly type: string; readonly study: { readonly __typename?: "Study"; readonly smid: string; }; readonly stackLayers: readonly { readonly __typename?: "Layer"; readonly stackSmid: string; readonly index: number; }[]; } | { ...; }'.
  const seriesSmid = imagingProvider.study.stackedFrames.find((s) => s.smid === stackSmid)?.series
    ?.smid;
  return imagingProvider.study.seriesList.find((s) => s.smid === seriesSmid);
};

/**
 * Encodes a stackSmid from the base and overlay viewport configurations
 */
export const encodeOverlayStackSmid = (baseStackSmid: string, overlayStackSmid: string): string => {
  return `${baseStackSmid}${UNICODE_GROUP_SEPARATOR}${overlayStackSmid}`;
};

/**
 * Decodes a overlaid stackSmid as a `stackLayers` object
 */
export const decodeOverlayStackSmidToStackLayers = (stackSmid: string): Layer[] =>
  stackSmid.split(UNICODE_GROUP_SEPARATOR).map((stackSmid: string, index: number) => ({
    stackSmid,
    index,
  }));

/** Checks if the stackSmid provided is present in any MultiStackImagingProvider */
export const isStackSmidInAnyMultiLayer = (
  stackSmid: string,
  stackProviders: TStackProviders
): boolean => {
  return Array.from(stackProviders).some(
    ([, provider]: [any, any]) =>
      provider instanceof MultiStackImagingProvider &&
      provider.getRefProviderByStackSmid(stackSmid) != null
  );
};

/** Checks if any multi-stack ImagingProvider instance is present */
export const isAnyMultiLayerRendered = (stackProviders: TStackProviders): boolean => {
  return Array.from(stackProviders).some(
    ([, provider]: [any, any]) => provider instanceof MultiStackImagingProvider
  );
};

/**
 * Checks if a stack SMID is a composite stack (concatenated matched series).
 * Composite SMIDs have the format: `composite:{topSmid}{SEP}{matchedSmid1}{SEP}...`
 */
export const isStackSmidComposite = (stackSmid?: string | null): boolean => {
  return stackSmid != null && stackSmid.startsWith(COMPOSITE_PREFIX);
};

/**
 * Encodes a composite stack SMID from the top stack and matched stack SMIDs.
 * Format: `composite:{topSmid}{SEP}{matchedSmid1}{SEP}{matchedSmid2}...`
 *
 * @param topStackSmid - The top (anchor) stack SMID
 * @param matchedStackSmids - Array of matched stack SMIDs in order
 * @returns Encoded composite stack SMID
 */
export const encodeCompositeStackSmid = (
  topStackSmid: string,
  matchedStackSmids: ReadonlyArray<string>
): string => {
  const allSmids = [topStackSmid, ...matchedStackSmids];
  return `${COMPOSITE_PREFIX}${allSmids.join(UNICODE_GROUP_SEPARATOR)}`;
};

/**
 * Decodes a composite stack SMID into the top and matched stack SMIDs.
 *
 * @param stackSmid - The encoded composite stack SMID
 * @returns Object with topSmid and matchedSmids array, or null if not a composite SMID
 */
export const decodeCompositeStackSmid = (
  stackSmid: string
): { topSmid: string; matchedSmids: ReadonlyArray<string> } | null => {
  if (!isStackSmidComposite(stackSmid)) {
    return null;
  }

  const withoutPrefix = stackSmid.slice(COMPOSITE_PREFIX.length);
  const smids = withoutPrefix.split(UNICODE_GROUP_SEPARATOR);

  if (smids.length < 1) {
    return null;
  }
  const [top, ...matched] = smids;
  return {
    topSmid: top,
    matchedSmids: matched,
  };
};

/**
 * Gets the top stack SMID from a composite stack SMID.
 * Returns the input if it's not a composite SMID.
 */
export const getTopStackSmidFromComposite = (stackSmid: string): string => {
  const decoded = decodeCompositeStackSmid(stackSmid);
  return decoded?.topSmid ?? stackSmid;
};

/**
 * Checks if the given stackSmid is associated with any composite stack provider.
 *
 * The stackSmid may be either:
 * - a synthetic composite SMID (e.g. the provider-map key or provider.compositeSmid), or
 * - a real stack SMID for the top stack or any constituent stack.
 */
export const isStackSmidInAnyComposite = (
  stackSmid: string,
  stackProviders: TStackProviders
): boolean => {
  return Array.from(stackProviders).some(
    ([providerSmid, provider]: [any, any]) =>
      typeof provider.isCompositeStack === 'function' &&
      provider.isCompositeStack() &&
      // Match against the synthetic composite SMID used as the provider key
      (providerSmid === stackSmid ||
        // Match against an explicit compositeSmid field when present
        provider.compositeSmid === stackSmid ||
        // Match against the top stack SMID
        provider.stack?.smid === stackSmid ||
        // Match against any constituent provider's stack SMID
        provider.getConstituentProviders?.()?.some((p: any) => p.stack?.smid === stackSmid))
  );
};

/**
 * Checks if any composite provider is present.
 */
export const isAnyCompositeRendered = (stackProviders: TStackProviders): boolean => {
  return Array.from(stackProviders).some(
    ([, provider]: [any, any]) =>
      typeof provider.isCompositeStack === 'function' && provider.isCompositeStack()
  );
};
